// src/Components/ShowFiles.jsx
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { auth } from "../utils/firebase";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  Modal,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "black",
  border: "2px solid #380093",
  boxShadow: 24,
  p: 4,
  color: "white",
};

function normalizeFileId(filename) {
  const stem =
    filename.lastIndexOf(".") > 0
      ? filename.substring(0, filename.lastIndexOf("."))
      : filename;
  return stem.replace(/ /g, "_").replace(/[^\w-]/g, "_");
}

export default function ShowFiles({
  uid,
  grantProposalForm_id,
  client_Organization_id,
  selectedDocs,
  setSelectedDocs,          // ✅ corrected prop name
  className = "",
}) {
  const location = useLocation();

  // Use provided props if present; otherwise fall back to auth/location
  const effectiveUid = uid || auth.currentUser?.uid || null;
  const effectiveGrantId =
    grantProposalForm_id ||
    location.state?.formDetails?.id ||
    location.state?.grantProposalForm_id;

  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/gptRun/get-documents-firestore`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              uid: effectiveUid,
              grantProposalForm_id: effectiveGrantId,
              client_Organization_id,
            }),
          }
        );

        const data = await res.json();
        if (res.ok && Array.isArray(data.documents)) {
          setFiles(data.documents);
        } else {
          setFiles([]);
        }
      } catch (err) {
        console.error("Error fetching documents:", err);
        setFiles([]);
      }
    })();
  }, [open, effectiveUid, effectiveGrantId, client_Organization_id]);

  return (
    <>
      <div
        className={[
          "z-50 bg-[#380093] border border-[#5406D2] text-sm px-4 py-1 rounded-sm",
          "hover:bg-[#5406D2] cursor-pointer",
          // default size/margins; can be overridden via className
          "w-[150px] mt-1 ml-[20px] mr-[20px]",
          className,
        ].join(" ")}
        onClick={() => setOpen(true)}
      >
        <button className="flex justify-evenly items-center w-full text-white">
          <span>Show Files</span>
          <InsertDriveFileOutlinedIcon />
        </button>
      </div>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" align="center" gutterBottom>
            Uploaded Documents
          </Typography>

          <List>
            {files.map((file) => {
              const fileName = typeof file === "string" ? file : file.name;
              const fileId = normalizeFileId(fileName);
              const isSelected = selectedDocs.includes(fileId);

              return (
                <ListItem
                  key={fileId}
                  button
                  onClick={() => {
                    setSelectedDocs((prev) =>
                      isSelected
                        ? prev.filter((id) => id !== fileId)
                        : [...prev, fileId]
                    );
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    sx={{
                      color: "white",
                      "&.Mui-checked": { color: "#9D4EDD" },
                    }}
                  />
                  <ListItemText primary={fileName} />
                </ListItem>
              );
            })}
          </List>

          <Box textAlign="center" mt={2}>
            <button
              onClick={() => setOpen(false)}
              className="bg-[#380093] text-white px-4 py-2 rounded text-sm hover:bg-[#5406D2]"
            >
              Close
            </button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

ShowFiles.propTypes = {
  uid: PropTypes.string,
  grantProposalForm_id: PropTypes.string,
  client_Organization_id: PropTypes.string.isRequired,
  selectedDocs: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedDocs: PropTypes.func.isRequired,
  className: PropTypes.string,
};
